
			<script lang="ts">
			import layout from '@/views/layouts/App/Main.vue'
			export default { layout }
			</script>
			<template>
		
    <div
        v-if="featuredProgram.program.data?.id"
        class="mx-auto mb-20 mt-16 max-w-7xl px-4 sm:mt-24 sm:px-6 lg:mt-32"
    >
        <Link
            :href="route('app.programs.show', { program: featuredProgram.program.data })"
            class="relative block w-full rounded-lg p-3 hover:shadow-lg"
        >
            <div class="lg:grid lg:grid-cols-12 lg:gap-8">
                <div class="sm:text-center md:mx-auto md:max-w-2xl lg:col-span-6 lg:text-left">
                    <h1>
                        <span
                            class="block text-sm font-semibold uppercase tracking-wide text-gray-500 sm:text-base lg:text-sm xl:text-base"
                        >
                            {{ trans('New Series') }}
                        </span>

                        <span
                            class="mt-1 block text-3xl font-extrabold tracking-tight sm:text-2xl xl:text-5xl"
                        >
                            <span class="block text-pink-500">
                                {{ featuredProgram.program.data.name }}
                            </span>
                        </span>
                    </h1>

                    <p
                        class="mt-3 text-base text-gray-500 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl"
                        v-html="featuredProgram.program.data.description"
                    />
                </div>

                <div
                    class="relative mt-12 sm:mx-auto sm:max-w-lg lg:col-span-6 lg:mx-0 lg:flex lg:max-w-none lg:items-start"
                >
                    <iframe
                        v-if="featuredProgram.source"
                        :id="`video-${featuredProgram.videoId}`"
                        :src="`${featuredProgram.source}?autoplay=1&muted=1`"
                        class="rounded-md"
                        width="100%"
                        height="100%"
                        allow="autoplay; fullscreen; picture-in-picture; muted;"
                        :autoplay="true"
                        :muted="true"
                    />
                </div>
            </div>
        </Link>
        <ProgramFeedAlert
            v-if="unreleasedWithFeed"
            class="my-6"
            :program="featuredProgram.program"
        />
    </div>

    <div class="py-12">
        <Container>
            <div v-for="collection in collections.data" :key="collection.id" class="mb-20">
                <h2 class="mb-8 text-3xl font-extrabold tracking-tight sm:text-4xl">
                    {{ trans(collection.name) }}
                </h2>
                <Carousel
                    :snap-align="snapAlign"
                    :breakpoints="breakpoints"
                    :transition="transition"
                    :class="(collection.programs ?? []).length < 4 ? 'shortCarousel' : ''"
                >
                    <Slide v-for="program in collection.programs" :key="program.id">
                        <div class="relative flex w-full flex-col gap-y-3">
                            <Card
                                :url="
                                    program.state !== 'prerelease'
                                        ? route('app.programs.show', { program: program })
                                        : undefined
                                "
                                :thumbnail-url="program.thumbnail_url"
                                :alt="program.name"
                            >
                                <template #addons>
                                    <span
                                        v-if="program.state === 'prerelease'"
                                        class="absolute left-auto right-16 top-8 h-16 w-16"
                                    >
                                        <span
                                            class="pointer-events-none absolute block w-44 rotate-45 transform bg-primary-400 text-center text-xs font-medium uppercase leading-relaxed text-white"
                                        >
                                            {{ trans('Coming Soon') }}
                                        </span>
                                    </span>

                                    <span
                                        v-if="program.access === 'free'"
                                        class="absolute left-auto right-16 top-8 h-16 w-16"
                                    >
                                        <span
                                            class="pointer-events-none absolute block w-44 rotate-45 transform bg-green-400 text-center text-xs font-medium uppercase leading-relaxed text-white"
                                        >
                                            {{ trans('Free') }}
                                        </span>
                                    </span>
                                </template>
                            </Card>
                        </div>
                    </Slide>

                    <template #addons>
                        <Navigation />
                    </template>
                </Carousel>
            </div>
        </Container>
    </div>
</template>

<script setup lang="ts">
    import { Link } from '@inertiajs/vue3'
    import { Carousel, Slide, Navigation } from 'vue3-carousel'
    import { useCarousel } from '@/scripts/hooks/useCarousel'
    import {
        CollectionModel,
        ModelResource,
        ProgramModel,
        ResourceCollection,
    } from '@/scripts/types/models'
    import Card from '@/views/partials/Common/Card.vue'
    import ProgramFeedAlert from '@/views/pages/App/Programs/Partials/ProgramFeedAlert.vue'
    import { onMounted, ref } from 'vue'
    import Container from '@/views/components/Layout/Container.vue'

    type ProgramType = {
        program: ModelResource<ProgramModel>
        source: string
        videoId: {
            type: string
            required: true
        }
    }

    const props = defineProps({
        featuredProgram: {
            type: Object as () => ProgramType,
            required: true,
        },
        collections: {
            type: Object as () => ResourceCollection<CollectionModel>,
            required: true,
        },
    })

    const unreleasedWithFeed = ref<boolean>(
        !props.featuredProgram.program.data.released &&
            props.featuredProgram.program.data?.release_at !== null &&
            (props.featuredProgram.program.data.feed?.is_active ?? false),
    )

    const { breakpoints, snapAlign, transition } = useCarousel()
    const iframeSrc = ref('')

    onMounted(() => {
        if (props.featuredProgram.source) {
            // Set the iframe source with autoplay query
            iframeSrc.value = `${props.featuredProgram.source}?autoplay=1&muted=1`

            // Optional: Force iframe to reload for autoplay to work
            const video = document.getElementById(
                `video-${props.featuredProgram.videoId}`,
            ) as HTMLIFrameElement
            if (video) {
                video.src = '' // Reset src
                setTimeout(() => {
                    video.src = `${props.featuredProgram.source}?autoplay=1&muted=1`
                }, 50) // Small delay for re-rendering
            }
        }
    })
</script>
